import React, { useEffect, useState } from 'react';

/** Pop-up d'installation PWA avec Axo animé. Détecte l'appareil :
 *  - PC / Android (Chromium) → bouton d'installation natif (beforeinstallprompt)
 *  - iPhone / iPad (iOS) → procédure manuelle (Partager → écran d'accueil)
 *  S'affiche à chaque connexion tant que l'app n'est pas installée. Voir CODEX §24. */

const AXO_IMG = '/vitrine-assets/axo.png?v=2';
const NEVER_KEY = 'axo_install_never';
const SEEN_KEY = 'axo_install_seen';

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
}
function isIOS() {
  const ua = navigator.userAgent || '';
  const iOSDevice = /iphone|ipad|ipod/i.test(ua);
  const iPadOS = navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1;
  return iOSDevice || iPadOS;
}

type Mode = 'install' | 'ios';

export default function InstallPrompt() {
  const [show, setShow] = useState(false);
  const [mode, setMode] = useState<Mode>('install');
  const [deferred, setDeferred] = useState<any>(null);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;                       // déjà installée
    if (localStorage.getItem(NEVER_KEY)) return;      // « ne plus afficher »
    if (sessionStorage.getItem(SEEN_KEY)) return;     // déjà vue cette session

    const onBIP = (e: any) => { e.preventDefault(); setDeferred(e); setMode('install'); setShow(true); };
    window.addEventListener('beforeinstallprompt', onBIP);
    const onInstalled = () => { setShow(false); sessionStorage.setItem(SEEN_KEY, '1'); };
    window.addEventListener('appinstalled', onInstalled);

    // iOS : pas de beforeinstallprompt → on propose la procédure manuelle
    let t: any;
    if (isIOS()) t = setTimeout(() => { setMode('ios'); setShow(true); }, 1600);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
      window.removeEventListener('appinstalled', onInstalled);
      if (t) clearTimeout(t);
    };
  }, []);

  const close = () => { setShow(false); sessionStorage.setItem(SEEN_KEY, '1'); };
  const never = () => { localStorage.setItem(NEVER_KEY, '1'); setShow(false); };

  const install = async () => {
    if (!deferred) return;
    setInstalling(true);
    try {
      deferred.prompt();
      await deferred.userChoice;
    } catch { /* ignore */ }
    setInstalling(false);
    setDeferred(null);
    close();
  };

  if (!show) return null;

  return (
    <div onClick={close} style={{ position: 'fixed', inset: 0, zIndex: 100000, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <style>{`
        @keyframes axoPop { 0%{opacity:0;transform:translateY(24px) scale(.92)} 100%{opacity:1;transform:none} }
        @keyframes axoBounce { 0%,100%{transform:translateY(0) rotate(-3deg)} 25%{transform:translateY(-10px) rotate(2deg)} 50%{transform:translateY(0) rotate(-2deg)} 75%{transform:translateY(-5px) rotate(3deg)} }
        @keyframes axoGlow { 0%,100%{box-shadow:0 0 0 0 rgba(124,58,237,.45)} 50%{box-shadow:0 0 0 14px rgba(124,58,237,0)} }
      `}</style>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 380, background: '#fff', borderRadius: 22, overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.35)', animation: 'axoPop .28s ease' }}>
        {/* Bandeau dégradé + Axo */}
        <div style={{ background: 'linear-gradient(135deg,#7c3aed,#e91e8c)', padding: '22px 22px 18px', textAlign: 'center', position: 'relative' }}>
          <button onClick={close} aria-label="Fermer" style={{ position: 'absolute', top: 12, right: 14, background: 'rgba(255,255,255,0.25)', border: 'none', color: '#fff', width: 28, height: 28, borderRadius: '50%', fontSize: 17, cursor: 'pointer', lineHeight: 1 }}>×</button>
          <img src={AXO_IMG} alt="Axo" draggable={false}
            style={{ width: 96, height: 96, borderRadius: '50%', background: '#fff', animation: 'axoBounce 2.2s ease-in-out infinite, axoGlow 2.2s ease-in-out infinite', display: 'inline-block' }} />
          <div style={{ color: '#fff', fontSize: 19, fontWeight: 800, marginTop: 12 }}>Installe NS'Live !</div>
          <div style={{ color: 'rgba(255,255,255,0.92)', fontSize: 13, marginTop: 4, lineHeight: 1.4 }}>
            Accède à tes lives en un tap et reçois tes notifications même quand l'app est fermée. 🚀
          </div>
        </div>

        {/* Corps : install native ou procédure iOS */}
        <div style={{ padding: '18px 20px 20px' }}>
          {mode === 'install' ? (
            <>
              <button onClick={install} disabled={installing}
                style={{ width: '100%', padding: '13px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg,#7c3aed,#e91e8c)', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', opacity: installing ? 0.7 : 1 }}>
                {installing ? 'Installation…' : '📲 Installer l\'application'}
              </button>
              <div style={{ textAlign: 'center', marginTop: 10, fontSize: 11.5, color: '#94a3b8' }}>Gratuit · aucune place prise sur le store</div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 13, color: '#475569', marginBottom: 12, fontWeight: 600 }}>📱 Sur iPhone / iPad, en 3 étapes :</div>
              <Step n="1">Appuie sur <b>Partager</b> <ShareIcon /> en bas de Safari</Step>
              <Step n="2">Choisis <b>« Sur l'écran d'accueil »</b> <span style={{ fontSize: 15 }}>➕</span></Step>
              <Step n="3">Appuie sur <b>« Ajouter »</b> en haut à droite</Step>
              <div style={{ marginTop: 12, padding: '9px 12px', background: '#f5f3ff', borderRadius: 10, fontSize: 12, color: '#7c3aed' }}>
                💡 Sur iPhone, l'installation se fait uniquement depuis <b>Safari</b> (pas Chrome).
              </div>
            </>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
            <button onClick={never} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>Ne plus afficher</button>
            <button onClick={close} style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Plus tard</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Step({ n, children }: { n: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 9 }}>
      <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%', background: '#7c3aed', color: '#fff', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{n}</span>
      <span style={{ fontSize: 13.5, color: '#0f172a', lineHeight: 1.5 }}>{children}</span>
    </div>
  );
}

function ShareIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" style={{ verticalAlign: 'middle', margin: '0 1px' }} fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 16V4M12 4l-4 4M12 4l4 4" /><path d="M5 12v7a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7" />
    </svg>
  );
}
